import { GridApi } from '@ag-grid-community/core'
import { RedrawRowsParams } from '@ag-grid-community/core/dist/cjs/gridApi'
import { Injectable } from '@angular/core'
import { ClearwaterTransaction } from '@helios/common-api'
import {
  ExternalTransaction,
  ExternalTransactionsService,
} from '../external-transactions.service'
import {
  FieldDifferenceMap,
  FieldDifferenceResults,
} from './difference-getter-helpers'

/**
 * This service is stateful, so it should be newed up per grid, ie. included in providers in @Component object.
 * This service is used to determine if fields have a difference between a clearwater and external source.
 */
@Injectable()
export class DifferenceGetterService {
  private readonly rowDifferenceMap: Map<
    string,
    Map<keyof ClearwaterTransaction, FieldDifferenceResults[]>
  > = new Map()

  private readonly cachedExternalTransactions: Map<
    number,
    ExternalTransaction
  > = new Map()

  constructor(private readonly service: ExternalTransactionsService) {}

  async setRowDifferences(
    data: ClearwaterTransaction[],
    gridApi: GridApi,
  ): Promise<void> {
    const extTranIds = this.getExternalTranIdsFromGridData(data)

    const extTrans = await this.getExternalTransactions(extTranIds)

    for (const row of data) {
      const id = row.rowId
      const diffMap = new Map<
        keyof ClearwaterTransaction,
        FieldDifferenceResults[]
      >()
      this.rowDifferenceMap.set(id, diffMap)

      for (const [cwTxField, hasDiffFunc] of FieldDifferenceMap) {
        const rowsExtTranIds = row.extTranIds
        if (rowsExtTranIds == null || rowsExtTranIds.length === 0) {
          diffMap.set(cwTxField, [
            {
              hasDifference: false,
              amount: undefined,
              dataSourceType: undefined,
            },
          ])
          continue
        }

        diffMap.set(
          cwTxField,
          hasDiffFunc(
            row,
            extTrans.filter(tran => rowsExtTranIds.includes(tran.extTranId)),
          ),
        )
      }
      this.refreshRow(id, gridApi)
    }
  }

  private refreshRow(rowId: string, gridApi: GridApi) {
    const rowNode = gridApi.getRowNode(rowId)
    if (rowNode) {
      const params: RedrawRowsParams = {
        rowNodes: [rowNode],
      }
      // Throwing redrawRows on its own thread to prevent UI blocking.
      setTimeout(() => {
        try {
          gridApi.redrawRows(params) // I tried refreshCells but the behavior was inconsistent.
        } catch (e) {
          //Swallow error. Grid was likely destroyed by the time we got here.
        }
      }, 1)
    }
  }

  /**
   * @internal
   * @param data
   */
  getExternalTranIdsFromGridData(data: ClearwaterTransaction[]): number[] {
    return Array.from(
      data
        .map(row => row.extTranIds)
        .filter((ids): ids is number[] => ids != null)
        .reduce((allIds, currIds) => {
          currIds.forEach(id => allIds.add(id))
          return allIds
        }, new Set<number>()),
    )
  }

  getHasDifference(
    rowId: string,
    field: keyof ClearwaterTransaction,
  ): FieldDifferenceResults[] {
    return (
      this.rowDifferenceMap.get(rowId)?.get(field) ?? [
        {
          hasDifference: false,
          amount: undefined,
          dataSourceType: undefined,
        },
      ]
    )
  }

  /**
   * @internal
   * @param extTranIds
   */
  async getExternalTransactions(
    extTranIds: number[],
  ): Promise<ExternalTransaction[]> {
    const trans: ExternalTransaction[] = []
    const notCachedIds = []

    for (const extTranId of extTranIds) {
      const tran = this.cachedExternalTransactions.get(extTranId)
      if (tran == null) {
        notCachedIds.push(extTranId)
        continue
      }
      trans.push(tran)
    }

    const newTrans = await this.service
      .getTransactionsByIds(notCachedIds)
      .toPromise()
    trans.push(...newTrans)

    newTrans.forEach(tran =>
      this.cachedExternalTransactions.set(tran.extTranId, tran),
    )

    return trans
  }
}
